/**
 * Centralized US date formatting helpers.
 *
 * Why: prior to Feb 2026 the codebase had ~6 ad-hoc copies of a "format an
 * ISO date as MM/DD/YYYY" function (toUSDate, fmtDate, formatDate, fmt, etc.)
 * scattered across pages. They all did the same thing but with subtle
 * differences in null-handling. This module is the single source of truth —
 * import from here instead of redefining inline.
 *
 * Accepted inputs:
 *   - ISO date string  ("2026-02-19")
 *   - ISO timestamp    ("2026-02-19T14:30:00Z")
 *   - JS Date object
 *   - null / undefined / empty string → returns the configured placeholder
 */

const _toIsoSlice = (input) => {
  if (input == null || input === "") return "";
  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) return "";
    // toISOString is always UTC; we only want the date portion which is
    // landlord-local-day-safe for our use-cases (dates entered via <input
    // type="date"> are stored as bare YYYY-MM-DD with no timezone shift).
    return input.toISOString().slice(0, 10);
  }
  const s = String(input);
  return s.slice(0, 10);
};

/**
 * Format an ISO-ish date as MM/DD/YYYY.
 *
 *   fmtUSDate("2026-02-19")              → "02/19/2026"
 *   fmtUSDate("2026-02-19T14:30:00Z")    → "02/19/2026"
 *   fmtUSDate(null)                      → "—"
 *   fmtUSDate("not a date", "N/A")       → "N/A"   (falls back to placeholder when input can't be parsed as YYYY-MM-DD)
 */
export function fmtUSDate(input, placeholder = "—") {
  const iso = _toIsoSlice(input);
  if (!iso) return placeholder;
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d || y.length !== 4) return placeholder;
  return `${m}/${d}/${y}`;
}

/**
 * Same as fmtUSDate but appends the time-of-day in 12-hour local format.
 *   fmtUSDateTime("2026-02-19T14:30:00Z")  → "02/19/2026 09:30 AM"   (in CST)
 * Useful for audit-log timestamps and "sent at" indicators.
 */
export function fmtUSDateTime(input, placeholder = "—") {
  if (input == null || input === "") return placeholder;
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return placeholder;
  const dateStr = fmtUSDate(d);
  const timeStr = d.toLocaleTimeString("en-US", {
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
  return `${dateStr} ${timeStr}`;
}
